/**
 * How the document viewer is filling the screen (OMA-672).
 *
 * - `native` — the Fullscreen API granted the viewer's root element.
 * - `fallback` — the API is missing (iPhone Safari) or refused the request
 *   (not triggered by a gesture, a permissions policy), so the viewer pins
 *   itself over the window with CSS instead. Esc is then the viewer's job,
 *   since no browser chrome will handle it.
 */
export type FullscreenMode = 'off' | 'native' | 'fallback';

/** The slice of `Element` this module touches — a fake in unit tests. */
export interface FullscreenTarget {
  requestFullscreen?: () => Promise<void>;
}

/** The slice of `Document` this module touches — a fake in unit tests. */
export interface FullscreenDocument {
  fullscreenElement: unknown;
  exitFullscreen?: () => Promise<void>;
}

/** The mode a press of the fullscreen button moves the viewer to. */
export async function toggleViewerFullscreen(
  current: FullscreenMode,
  target: FullscreenTarget,
  doc: FullscreenDocument
): Promise<FullscreenMode> {
  if (current === 'fallback') return 'off';

  if (current === 'native' || doc.fullscreenElement === target) {
    try {
      await doc.exitFullscreen?.();
    } catch {
      // Already left (Esc raced the click) — nothing to undo.
    }
    return 'off';
  }

  if (typeof target.requestFullscreen !== 'function') return 'fallback';

  try {
    await target.requestFullscreen();
    return 'native';
  } catch {
    return 'fallback';
  }
}

/**
 * The mode after a `fullscreenchange` event. The browser exits native
 * fullscreen on its own — Esc, F11, a tab switch — and the viewer only learns
 * of it here, so a stale `native` would leave the exit icon showing on a
 * viewer that is back inside its dialog.
 */
export function fullscreenModeAfterChange(
  current: FullscreenMode,
  target: FullscreenTarget,
  doc: FullscreenDocument
): FullscreenMode {
  if (current !== 'native') return current;
  return doc.fullscreenElement === target ? 'native' : 'off';
}
